import { useContext, useMemo } from 'react';
import { AppLocalizationModel, AppUserInfo } from '@/models/AppViewModel';
import { menuNameKeyMap } from '@/constants/menuNameKeyMap';
import i18n from '@/i18n';
import { UserContext, type UserContextInterface } from '@/contexts/userContext';

type UserContextWithAppUser = Omit<UserContextInterface, 'userInfo'> & {
  userInfo: AppUserInfo | null | undefined;
};

/**
 * Backend key → frontend key. Use when the API uses a different key than we show in the UI.
 * Add entries here for any term that the backend uses under a different name.
 */
const BACKEND_TO_FRONTEND_OVERRIDES: Record<string, string> = {
  'Portfolio Diversification': 'Asset Allocation',
  'AUM/Held Away': 'AUM & Held Away',
};

/**
 * Frontend key → backend key. Only entries where the backend expects a different key.
 * Add entries here for any term that the backend uses under a different name.
 */
export const FRONTEND_TO_BACKEND_OVERRIDES: Record<string, string> = {
  'Asset Allocation': 'Portfolio Diversification',
  'AUM & Held Away': 'AUM/Held Away',
};

function normalizeTOptions(
  options?: Record<string, unknown> | string,
  interpolation?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (typeof options === 'string') {
    return { ...(interpolation ?? {}), defaultValue: options };
  }
  if (options != null && Object.keys(options).length > 0) {
    return options;
  }
  if (interpolation != null && Object.keys(interpolation).length > 0) {
    return interpolation;
  }
  return undefined;
}

/**
 * Resolves a menu/term key or raw i18n key to a localized string.
 * Exported for unit testing.
 */
export function getLocalizedString(
  key: string,
  locale?: AppLocalizationModel[],
  options?: Record<string, unknown> | string,
  interpolation?: Record<string, unknown>,
): string {
  const normalizedOptions = normalizeTOptions(options, interpolation);

  if (normalizedOptions !== undefined) {
    return i18n.t(key, normalizedOptions as Record<string, string>);
  }
  // Resolve menu terms first so we never call i18n.t(key) when key is a top-level namespace object (e.g. "documents"); menu keys point to leaf keys like "documents.title".
  const maybeTermKey = BACKEND_TO_FRONTEND_OVERRIDES[key] ?? key;
  const i18nKey = menuNameKeyMap[maybeTermKey] as string | undefined;
  if (i18nKey !== undefined) {
    const backendKeyForLocale =
      FRONTEND_TO_BACKEND_OVERRIDES[maybeTermKey] ?? maybeTermKey;
    const localeItem = locale?.find(
      (item) =>
        item.Key !== undefined &&
        (item.Key === maybeTermKey || item.Key === backendKeyForLocale),
    );
    if (localeItem?.Value) return localeItem.Value;
    const value = i18n.t(i18nKey);
    return typeof value === 'string'
      ? value
      : i18n.t(i18nKey, { defaultValue: key });
  }
  if (i18n.exists(key)) {
    const value = i18n.t(key);
    return typeof value === 'string'
      ? value
      : i18n.t(key, { defaultValue: key });
  }
  const value = i18n.t(key);
  return typeof value === 'string' ? value : key;
}

type LocalizationT = (
  key: string,
  options?: Record<string, unknown> | string,
  interpolation?: Record<string, unknown>,
) => string;

interface UseLocalizationReturn {
  /** Translation function. Accepts frontend or backend menu keys, or raw i18n keys. Configurable terms (Asset Allocation, Trading Activity, Cash Flow) respect firm overrides; raw i18n keys bypass firm overrides. Optional second argument: interpolation object or default value string. */
  t: LocalizationT;
  locale: AppLocalizationModel[] | undefined;
}

/**
 * Hook for i18n + firm locale overrides. Prefer this over useTranslation when you need
 * configurable terms (e.g. Asset Allocation, Cash Flow) that respect userInfo.Locale.
 */
export function useLocalization(): UseLocalizationReturn {
  const { userInfo } = useContext(UserContext) as UserContextWithAppUser;
  const locale = userInfo?.Locale;
  const t = useMemo(
    () =>
      (
        key: string,
        options?: Record<string, unknown> | string,
        interpolation?: Record<string, unknown>,
      ) =>
        getLocalizedString(key, locale, options, interpolation),
    [locale],
  );
  return {
    t,
    locale,
  };
}
